import { useEffect, useRef } from 'react';

import { useAnalytics } from '@/lib/analytics/use-analytics';

import { ProjectDetailSkeleton } from './project-detail-skeleton';

type PersonaClientLike = {
  open: () => void;
  destroy: () => void;
};

/** Persona's hard cap for the embedded iframe width. */
const MODAL_FRAME_WIDTH = '768px';
/** Tall enough for document capture, but never touching the viewport edges. */
const MODAL_FRAME_HEIGHT = 'min(880px, 90vh)';

/**
 * Opens a server-created Persona inquiry in the embedded Persona flow.
 *
 * The inquiry (and its session token) come from core-api, so the browser
 * never sees a template or environment id. The SDK is imported lazily: it is
 * only needed on this step, and keeping it out of the main chunk matches how
 * Plaid Link is loaded on demand today.
 *
 * `onSuccess` fires when the participant finishes the flow; Persona's actual
 * verdict arrives later via webhook, exactly like Plaid.
 */
export function PersonaVerificationContent({
  inquiryId,
  sessionToken,
  environmentId,
  projectId,
  userId,
  onSuccess,
  onCancel,
}: {
  inquiryId: string;
  sessionToken: string;
  /**
   * Persona environment the inquiry lives in. The SDK defaults to the first
   * production environment, so without this a sandbox inquiry fails to open.
   */
  environmentId?: string;
  projectId: string;
  userId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const analytics = useAnalytics();
  // Strict-Mode double-mount guard: one client, one `started` event.
  const clientRef = useRef<PersonaClientLike | null>(null);
  // Latest callbacks without re-creating the client when a parent re-renders.
  const callbacksRef = useRef({ onSuccess, onCancel });
  callbacksRef.current = { onSuccess, onCancel };

  useEffect(() => {
    let cancelled = false;

    if (clientRef.current) return;

    analytics?.track({
      action: 'started',
      actionSubject: 'personaIdVerification',
      attributes: { projectId, userId, inquiryId },
    });

    void (async () => {
      const { Client } = await import('persona');
      if (cancelled) return;

      const client = new Client({
        inquiryId,
        sessionToken,
        environmentId,
        // The SDK caps its modal at 400x650 on desktop, which reads as a
        // phone screen floating in the page. Widen to the SDK's 768px limit
        // and let it use most of the viewport height. Small screens ignore
        // both and go full-screen. Note the template's own preferredDimensions
        // (if set in Persona) are applied on ready and can clamp these.
        frameWidth: MODAL_FRAME_WIDTH,
        frameHeight: MODAL_FRAME_HEIGHT,
        onReady: () => client.open(),
        onComplete: () => {
          callbacksRef.current.onSuccess();
        },
        onCancel: () => {
          callbacksRef.current.onCancel();
        },
        onError: (error) => {
          analytics?.track({
            action: 'errored',
            actionSubject: 'personaIdVerification',
            attributes: { projectId, userId, inquiryId, error: error.code },
          });
          callbacksRef.current.onCancel();
        },
      });

      clientRef.current = client;
    })();

    return () => {
      cancelled = true;
      clientRef.current?.destroy();
      clientRef.current = null;
    };
  }, [analytics, inquiryId, sessionToken, environmentId, projectId, userId]);

  return <ProjectDetailSkeleton />;
}
